/**
 * Geography > Plants (WPS-222). A plant is a `GeoZone` with
 * `zone_type = "plant_service_area"`, so this reuses the zone CRUD pinned to
 * that type - moved here from Settings > Plants, one implementation.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Factory, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useCreateGeoZone,
  useDeleteGeoZone,
  useGeoZones,
  useUpdateGeoZone,
  type GeoZone,
} from '@/admin/hooks/use-geo'

import { CodeCell, GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine } from './geography-utils'
import { saveWithFieldErrors, useListState } from './section-crud'

const PLANT_ZONE_TYPE = 'plant_service_area'

export function PlantsSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('geo-zones.create')
  const canEdit = can('geo-zones.edit')
  const canDelete = can('geo-zones.delete')

  const list = useListState()
  const params = useMemo(() => ({ ...list.params, zone_type: PLANT_ZONE_TYPE }), [list.params])
  const { data, isLoading, isFetching, isError, refetch } = useGeoZones(params)
  const createPlant = useCreateGeoZone()
  const updatePlant = useUpdateGeoZone()
  const deletePlant = useDeleteGeoZone()

  const [editing, setEditing] = useState<GeoZone | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<GeoZone | null>(null)

  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.plants.countOne', 'plant'),
    t('geography.plants.countMany', 'plants'),
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'name',
        display_name: t('geography.plants.field.name', 'Plant name'),
        field_type: 'TEXT',
        required: true,
      },
      {
        code_name: 'code',
        display_name: t('geography.plants.field.code', 'Plant code'),
        field_type: 'TEXT',
        required: false,
      },
    ],
    [t],
  )
  const values = useMemo<Record<string, unknown>>(
    () => (editing ? { name: editing.name, code: editing.code } : {}),
    [editing],
  )

  const columns = useMemo<DataTableColumn<GeoZone>[]>(
    () => [
      {
        id: 'name',
        header: t('geography.plants.column.plant', 'Plant'),
        sortable: true,
        sortField: 'name',
        cell: (r) => <TextCell value={r.name} />,
      },
      { id: 'code', header: t('geography.common.code', 'Code'), cell: (r) => <CodeCell value={r.code} /> },
      {
        id: 'postal_code_count',
        header: t('geography.plants.column.postalCodes', 'Postal codes'),
        cell: (r) => <TextCell muted value={r.postal_code_count.toLocaleString()} />,
      },
      {
        id: 'psa_count',
        header: t('geography.plants.column.zonesServed', 'Zones served'),
        cell: (r) => <TextCell muted value={r.psa_count.toLocaleString()} />,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setEditing(r)
                    setFormOpen(true)
                  }}
                >
                  {t('geography.common.edit', 'Edit')}
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                  {t('geography.common.delete', 'Delete')}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canDelete, canEdit, t],
  )

  return (
    <>
      <GeographySectionShell
        testId="geography-plants"
        title={t('geography.plants.title', 'Plants')}
        description={t(
          'geography.plants.description',
          'Manufacturing plants and their service areas. Zones map to a plant per business line, and product availability is declared against plants.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.plants.searchPlaceholder', 'Search plants')}
        actions={
          canCreate ? (
            <Button
              size="sm"
              data-testid="geography-plants-add-btn"
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('geography.plants.add', 'Add plant')}
            </Button>
          ) : null
        }
      >
        <DataTable<GeoZone>
          tableId="geography-plants"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState
              icon={Factory}
              title={t('geography.plants.emptyTitle', 'No plants')}
              description={t(
                'geography.plants.emptyDescription',
                'Add the first plant so zones and product availability can reference it.',
              )}
            />
          }
        />
      </GeographySectionShell>

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        title={
          editing
            ? t('geography.plants.editTitle', 'Edit plant')
            : t('geography.plants.addTitle', 'Add plant')
        }
        fields={fields}
        values={values}
        isPending={createPlant.isPending || updatePlant.isPending}
        onSave={(v, _reason, helpers) =>
          saveWithFieldErrors(
            async () => {
              const payload = {
                name: String(v.name ?? '').trim(),
                code: String(v.code ?? '').trim(),
              }
              if (editing) {
                await updatePlant.mutateAsync({ uuid: editing.uuid, data: payload })
              } else {
                await createPlant.mutateAsync({ ...payload, zone_type: PLANT_ZONE_TYPE })
              }
            },
            helpers,
            editing
              ? t('geography.plants.updated', 'Plant updated.')
              : t('geography.plants.created', 'Plant added.'),
            t('geography.plants.saveFailed', 'Could not save the plant.'),
          )
        }
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={t('geography.plants.deleteTitle', 'Delete plant?')}
        description={
          deleting
            ? t('geography.plants.deleteDescription', 'Delete {{name}}? This cannot be undone.', {
                name: deleting.name,
              })
            : undefined
        }
        isPending={deletePlant.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await deletePlant.mutateAsync(deleting.uuid)
            toast.success(t('geography.plants.deleted', 'Plant deleted.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(err, t('geography.plants.deleteFailed', 'Could not delete the plant.'))
          }
        }}
      />
    </>
  )
}
